import React, { useMemo, useState } from 'react';
import { FlexTableV2 } from 'components/FlexTableV2';
import { getSortedRowModel } from '@tanstack/react-table';
import { Line, Pie } from 'react-chartjs-2';
import * as S from '../../styles';
import { matrixColumnsConfig } from '../../logic';

const generateColors = (count) => {
  const colors = [
    '#007bff',
    '#6610f2',
    '#6f42c1',
    '#e83e8c',
    '#dc3545',
    '#fd7e14',
    '#ffc107',
    '#28a745',
    '#20c997',
    '#17a2b8'
  ];
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

export const TelephonyAnalyticsDashboard = ({
  queueMonitoringData,
  tableState
}) => {
  const reasonData = queueMonitoringData?.reasonsRanking || [];
  const ivrData = queueMonitoringData?.ivrStatistics || [];
  const matrixData = queueMonitoringData?.statisticsByHour || [];

  const matrixColumns = useMemo(() => matrixColumnsConfig, []);

  const filteredMatrixData = useMemo(() => {
    return matrixData.filter(
      (item) => (item.qtdAnswered || 0) > 0 || (item.qtdAbandoned || 0) > 0
    );
  }, [matrixData]);

  const [selectedReason, setSelectedReason] = useState(null);

  const topReasonData = useMemo(() => {
    return reasonData.slice(0, 10);
  }, [reasonData]);

  const pieChartData = useMemo(() => {
    const labels = topReasonData.map((r) => r.name);
    const data = topReasonData.map((r) => r.value);
    const backgroundColors = generateColors(topReasonData.length);

    const finalBackgroundColors = backgroundColors.map((color, index) => {
      const reasonName = topReasonData[index]?.name;
      if (selectedReason && selectedReason.name !== reasonName) {
        return color + '33';
      }
      return color;
    });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: finalBackgroundColors,
          hoverBackgroundColor: finalBackgroundColors
        }
      ]
    };
  }, [topReasonData, selectedReason]);

  const pieOptions = {
    legend: {
      display: true,
      position: 'right',
      labels: {
        fontSize: 14,
        boxWidth: 10,
        padding: 10
      }
    },
    onClick: (e, elements) => {
      if (elements && elements.length > 0) {
        const index = elements[0]._index;
        const reason = topReasonData[index];
        setSelectedReason(reason);
      } else {
        setSelectedReason(null);
      }
    },
    maintainAspectRatio: false
  };

  const lineChartData = useMemo(() => {
    // Cria array com as 24 horas do dia (0 a 23)
    const hours = Array.from({ length: 24 }, (_, i) => i);

    const labels = hours.map((hour) => {
      const nextHour = (hour + 1) % 24;
      const fmt = (n) => n.toString().padStart(2, '0');
      return `${fmt(hour)}:00 - ${fmt(nextHour)}:00`;
    });

    const answeredVolume = [];
    const abandonedVolume = [];
    const projectedVolume = [];

    hours.forEach((hour) => {
      // Tenta encontrar o dado correspondente à hora atual no matrixData
      // A string de intervalo geralmente vem como "HH:00 às HH:00" ou similar
      // Vamos tentar comparar o inicio da string com a hora formatada
      const hourStr = hour.toString().padStart(2, '0');

      const foundItem = matrixData.find((item) => {
        const intervalStart = item.interval?.split(' ')?.[0]?.split(':')?.[0]; // Pega o "HH" do "HH:00"
        return intervalStart === hourStr;
      });

      if (foundItem) {
        answeredVolume.push(foundItem.qtdAnswered || 0);
        abandonedVolume.push(foundItem.qtdAbandoned || 0);
        projectedVolume.push(foundItem.projectedVolume || 0);
      } else {
        answeredVolume.push(0);
        abandonedVolume.push(0);
        projectedVolume.push(0);
      }
    });

    return {
      labels,
      datasets: [
        {
          label: 'Volume Atendido',
          data: answeredVolume,
          fill: false,
          borderColor: 'rgb(19,133,77)',
          backgroundColor: 'rgb(19,133,77)',
          tension: 0.3
        },
        {
          label: 'Volume Não Atendido',
          data: abandonedVolume,
          fill: false,
          borderColor: '#dc3545',
          backgroundColor: '#dc3545',
          tension: 0.3
        },
        {
          label: 'Volume Projetado',
          data: projectedVolume,
          fill: false,
          borderColor: '#11cdef',
          backgroundColor: '#11cdef',
          borderDash: [5, 5],
          tension: 0.3
        }
      ]
    };
  }, [matrixData]);

  const lineOptions = {
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        { ticks: { beginAtZero: true }, gridLines: { color: '#f0f0f0' } }
      ],
      xAxes: [{ gridLines: { display: false } }]
    },
    legend: { position: 'bottom' }
  };

  const displayedSubReasons = useMemo(() => {
    if (selectedReason) {
      return (selectedReason.subreasons || []).sort(
        (a, b) => b.value - a.value
      );
    }

    const all = reasonData.flatMap((r) =>
      (r.subreasons || []).map((sub) => ({
        ...sub,
        displayName: `${r.name} - ${sub.name}`
      }))
    );
    return all.sort((a, b) => b.value - a.value);
  }, [selectedReason, reasonData]);

  const ivrRanking = useMemo(() => {
    return [...ivrData]
      .sort((a, b) => Number(b.count) - Number(a.count))
      .slice(0, 10);
  }, [ivrData]);

  return (
    <S.AnalyticsSection>
      <S.MatrixTableWrapper>
        <h4>Matriz Horária de Indicadores</h4>
        <div className="flex-table-container">
          <FlexTableV2
            table={{
              columns: matrixColumns,
              data: filteredMatrixData,
              rowCount: filteredMatrixData.length,
              getSortedRowModel: getSortedRowModel(),
              enablePagination: false,
              enableColumnOrdering: true,
              enableHiding: true,
              manualSorting: false,
              enableLocalStorageMemoWithKey: 'monitoring-telephony-matrix',
              scrollContainerHeight: 'unset'
            }}
            state={tableState}
          />
        </div>
      </S.MatrixTableWrapper>

      <S.AnalyticsRow>
        <S.AnalyticsCard style={{ flex: 2 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}
          >
            <h4>Ranking de Motivos (Top 10 - Gráfico)</h4>
            {selectedReason && (
              <button
                onClick={() => setSelectedReason(null)}
                style={{
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  textDecoration: 'underline'
                }}
              >
                Limpar filtro
              </button>
            )}
          </div>
          <div style={{ flex: 1, minHeight: '250px' }}>
            {reasonData.length > 0 ? (
              <Pie data={pieChartData} options={pieOptions} />
            ) : (
              <div
                style={{ padding: '2rem', textAlign: 'center', color: '#888' }}
              >
                Sem dados de motivos
              </div>
            )}
          </div>
        </S.AnalyticsCard>

        <S.AnalyticsCard style={{ flex: 1 }}>
          <h4>Ranking de Motivos (Top 10 - Lista)</h4>
          <S.RankingList>
            {topReasonData.length > 0 ? (
              topReasonData.map((reason, idx) => (
                <li
                  key={idx}
                  onClick={() => setSelectedReason(reason)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      selectedReason?.name === reason.name
                        ? '#f0f8ff'
                        : 'transparent'
                  }}
                >
                  <span className="label">{reason.name}</span>
                  <span className="value">{reason.value}</span>
                </li>
              ))
            ) : (
              <li style={{ color: '#999' }}>Nenhum motivo</li>
            )}
          </S.RankingList>
        </S.AnalyticsCard>
      </S.AnalyticsRow>

      <S.AnalyticsRow>
        <S.AnalyticsCard style={{ flex: 1 }}>
          <h4>
            Submotivos:{' '}
            <span style={{ fontWeight: 'normal' }}>
              {selectedReason ? selectedReason.name : 'Geral'}
            </span>
          </h4>
          <S.RankingList>
            {displayedSubReasons.length > 0 ? (
              displayedSubReasons.slice(0, 10).map((sub, idx) => (
                <li key={idx}>
                  <span className="label">{sub.displayName || sub.name}</span>
                  <span className="value">{sub.value}</span>
                </li>
              ))
            ) : (
              <li style={{ color: '#999' }}>Nenhum submotivo</li>
            )}
          </S.RankingList>
        </S.AnalyticsCard>

        <S.AnalyticsCard style={{ flex: 1 }}>
          <h4>Ranking de Origem (URA)</h4>
          <S.RankingList>
            {ivrRanking.length > 0 ? (
              ivrRanking.map((item, idx) => (
                <li key={idx}>
                  <span className="label" title={`${item.ivr} - ${item.queue}`}>
                    {item.option || item.ivr || 'Desconhecido'}
                  </span>
                  <span className="value">{item.count}</span>
                </li>
              ))
            ) : (
              <li style={{ color: '#999' }}>Sem dados de URA</li>
            )}
          </S.RankingList>
        </S.AnalyticsCard>
      </S.AnalyticsRow>

      <S.AnalyticsRow>
        <S.AnalyticsCard style={{ width: '100%' }}>
          <h4>Volume Projetado x Realizado</h4>
          <div style={{ height: '300px', width: '100%' }}>
            <Line data={lineChartData} options={lineOptions} />
          </div>
        </S.AnalyticsCard>
      </S.AnalyticsRow>
    </S.AnalyticsSection>
  );
};
